import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { trpc } from '../../lib/trpc';
import { PageContainer } from '../../components/PageContainer';
import { PageLoading } from '../../components/ui/LoadingSpinner';
import { ErrorDisplay } from '../../components/ui/ErrorDisplay';
import { Toggle } from '../../components/ui/Toggle';
import { ConfirmationModal } from '../../components/ui/ConfirmationModal';
import { useAuth } from '../../hooks/useAuth';
import { UserRole, ROLE_LEVELS } from '@wineryxplorer/shared';
import { AdminUserAccountInfo } from './AdminUserAccountInfo';
import { AdminUserDetailHeader } from './AdminUserDetailHeader';
import { AdminUserRoleSelectorModal } from './AdminUserRoleSelectorModal';
import { FormSection } from '../../components/ui/FormSection';
import { DeleteUserDialog } from './DeleteUserDialog';
import { PendingDeletionBanner } from './PendingDeletionBanner';

export const AdminUserDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { user: currentUser } = useAuth();
  const utils = trpc.useUtils();
  const navigate = useNavigate();

  const { data: user, isLoading, error, refetch } = trpc.adminUsers.getById.useQuery(
    { id: id! },
    { enabled: !!id }
  );

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [publicProfile, setPublicProfile] = useState(false);

  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRoleSelector, setShowRoleSelector] = useState(false);
  const [showRoleConfirmation, setShowRoleConfirmation] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);

  // Sync form state when user data loads
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email);
      setUserName(user.userName);
      setEmailVerified(user.emailVerified);
      setPhoneVerified(user.phoneVerified);
      setOnboardingComplete(user.onboardingComplete);
      setPublicProfile(user.publicProfile);
    }
  }, [user]);

  const updateMutation = trpc.adminUsers.update.useMutation({
    onSuccess: (result) => {
      setSaveError(null);
      if (result.changedFields.length > 0) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        utils.adminUsers.getById.invalidate({ id: id! });
        utils.adminUsers.list.invalidate();
      }
    },
    onError: (err) => {
      setSaveError(err.message);
      setSaveSuccess(false);
    },
  });

  const deactivateMutation = trpc.adminUsers.deactivate.useMutation({
    onSuccess: () => {
      setShowDeactivateModal(false);
      utils.adminUsers.getById.invalidate({ id: id! });
      utils.adminUsers.list.invalidate();
    },
  });

  const reactivateMutation = trpc.adminUsers.reactivate.useMutation({
    onSuccess: () => {
      setShowReactivateModal(false);
      utils.adminUsers.getById.invalidate({ id: id! });
      utils.adminUsers.list.invalidate();
    },
  });

  const changeRoleMutation = trpc.adminUsers.changeRole.useMutation({
    onSuccess: () => {
      setShowRoleConfirmation(false);
      setSelectedRole(null);
      utils.adminUsers.getById.invalidate({ id: id! });
      utils.adminUsers.list.invalidate();
    },
  });

  const handleSave = () => {
    if (!id) return;
    setSaveError(null);
    setSaveSuccess(false);
    updateMutation.mutate({
      id,
      name: name || undefined,
      email,
      userName,
      emailVerified,
      phoneVerified,
      onboardingComplete,
      publicProfile,
    });
  };

  const isDeactivated = user?.deactivated ?? false;

  // Can the current user deactivate/reactivate this user? Not while a deletion
  // is pending: Restore owns the account's state until it is resolved.
  const canManageActivation =
    currentUser &&
    user &&
    currentUser.id !== user._id &&
    ROLE_LEVELS[currentUser.role] > ROLE_LEVELS[user.role as UserRole] &&
    !user.pendingDeletion;

  // Only SUPER_ADMIN may delete; a pendingDeletion marker (even on an `active`
  // user left by a crashed restore) must be resolved via Restore first.
  const canDelete =
    !!currentUser &&
    !!user &&
    currentUser.role === UserRole.SUPER_ADMIN &&
    currentUser.id !== user._id &&
    user.role !== UserRole.SUPER_ADMIN &&
    user.status === 'active' &&
    !user.pendingDeletion;

  // Compute promotable roles for role change
  const getPromotableRoles = (): UserRole[] => {
    if (!currentUser || !user) return [];
    if (currentUser.id === user._id) return [];
    if (ROLE_LEVELS[currentUser.role] <= ROLE_LEVELS[user.role as UserRole]) return [];

    const maxLevel =
      currentUser.role === UserRole.SUPER_ADMIN
        ? ROLE_LEVELS[UserRole.SITE_ADMIN]
        : currentUser.role === UserRole.SITE_ADMIN
          ? ROLE_LEVELS[UserRole.VENUE_ADMIN]
          : 0;

    return [UserRole.USER, UserRole.VENUE_ADMIN, UserRole.SITE_ADMIN].filter(
      (r) => ROLE_LEVELS[r] <= maxLevel && r !== user.role
    );
  };

  const promotableRoles = getPromotableRoles();
  const canChangeRole = promotableRoles.length > 0 && !isDeactivated;

  if (isLoading) {
    return (
      <PageContainer>
        <PageLoading message="Loading user..." />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
          <ErrorDisplay
          title="Failed to load user"
          message={error.message}
          showRetry
          onRetry={() => refetch()}
        />
      </PageContainer>
    );
  }

  if (!user) {
    return (
      <PageContainer>
          <ErrorDisplay title="User not found" message="The requested user does not exist." />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
        {/* Header with avatar, name, role, status */}
        <AdminUserDetailHeader
          name={user.name}
          userName={user.userName}
          role={user.role as UserRole}
          image={user.image}
          avatar={user.avatar}
          isDeactivated={isDeactivated}
          canManageActivation={!!canManageActivation}
          canChangeRole={canChangeRole}
          canDelete={canDelete}
          onChangeRole={() => setShowRoleSelector(true)}
          onDeactivate={() => setShowDeactivateModal(true)}
          onReactivate={() => setShowReactivateModal(true)}
          onDelete={() => setShowDeleteDialog(true)}
        />

        {user.pendingDeletion && (
          <PendingDeletionBanner
            userId={user._id}
            pendingDeletion={user.pendingDeletion}
            canRestore={currentUser?.role === UserRole.SUPER_ADMIN}
          />
        )}

        {/* Edit form */}
        <div className="bg-gray-50 rounded-lg border border-gray-200">
          {/* Action bar at top */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">User Details</h2>
            <div className="flex items-center gap-3">
              {saveError && (
                <span className="text-sm text-red-600">{saveError}</span>
              )}
              {saveSuccess && (
                <span className="text-sm text-green-600">Saved successfully</span>
              )}
              {!isDeactivated && (
                <button
                  onClick={handleSave}
                  disabled={updateMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 text-sm font-medium"
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              )}
            </div>
          </div>

          <div className="p-6 space-y-6">
            {isDeactivated && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
                This account is deactivated. Fields cannot be edited.
              </div>
            )}

            {/* Text fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isDeactivated}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-info-500 focus:ring-1 focus:ring-info-500 outline-none disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  disabled={isDeactivated}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-info-500 focus:ring-1 focus:ring-info-500 outline-none disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isDeactivated}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-info-500 focus:ring-1 focus:ring-info-500 outline-none disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>
            </div>

            {/* Toggle fields */}
            <FormSection title="Account Settings" titleAs="h3" contentClassName="space-y-1">
              <Toggle
                checked={emailVerified}
                onChange={setEmailVerified}
                disabled={isDeactivated}
                label="Email Verified"
                description="Whether the user's email address has been verified"
              />
              <Toggle
                checked={phoneVerified}
                onChange={setPhoneVerified}
                disabled={isDeactivated}
                label="Phone Verified"
                description="Whether the user's phone number has been verified"
              />
              <Toggle
                checked={onboardingComplete}
                onChange={setOnboardingComplete}
                disabled={isDeactivated}
                label="Onboarding Complete"
                description="Whether the user has completed the onboarding flow"
              />
              <Toggle
                checked={publicProfile}
                onChange={setPublicProfile}
                disabled={isDeactivated}
                label="Public Profile"
                description="Whether the user's profile is publicly visible"
              />
            </FormSection>

            <AdminUserAccountInfo
              userId={user._id}
              role={user.role as UserRole}
              createdAt={user.createdAt}
              updatedAt={user.updatedAt}
              assignedVenues={user.assignedVenues}
            />
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeactivateModal}
        onClose={() => setShowDeactivateModal(false)}
        onConfirm={() => deactivateMutation.mutate({ id: id! })}
        title="Deactivate Account"
        message={`Are you sure you want to deactivate ${user.name || user.userName}'s account? They will no longer be able to log in.`}
        confirmText="Deactivate"
        isDestructive
        isLoading={deactivateMutation.isPending}
      />

      <ConfirmationModal
        isOpen={showReactivateModal}
        onClose={() => setShowReactivateModal(false)}
        onConfirm={() => reactivateMutation.mutate({ id: id! })}
        title="Reactivate Account"
        message={`Are you sure you want to reactivate ${user.name || user.userName}'s account? They will be able to log in again.`}
        confirmText="Reactivate"
        isLoading={reactivateMutation.isPending}
      />

      <DeleteUserDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        userId={user._id}
        userEmail={user.email}
        onDeleted={(outcome) => {
          setShowDeleteDialog(false);
          if (outcome === 'wiped') navigate('/admin/users');
        }}
      />

      {/* Role Selector Modal (step 1) */}
      {showRoleSelector && (
        <AdminUserRoleSelectorModal
          userLabel={user.name || user.userName}
          currentRole={user.role as UserRole}
          promotableRoles={promotableRoles}
          selectedRole={selectedRole}
          onSelectRole={setSelectedRole}
          onCancel={() => {
            setShowRoleSelector(false);
            setSelectedRole(null);
          }}
          onContinue={() => {
            setShowRoleSelector(false);
            setShowRoleConfirmation(true);
          }}
        />
      )}

      {/* Role Change Confirmation Modal (step 2) */}
      <ConfirmationModal
        isOpen={showRoleConfirmation && !!selectedRole}
        onClose={() => {
          setShowRoleConfirmation(false);
          setSelectedRole(null);
        }}
        onConfirm={() => {
          if (selectedRole) {
            changeRoleMutation.mutate({ id: id!, newRole: selectedRole });
          }
        }}
        title="Confirm Role Change"
        message={`Are you sure you want to change ${user.name || user.userName}'s role from ${(user.role as string).replace('_', ' ')} to ${selectedRole?.replace('_', ' ') || ''}?`}
        confirmText="Change Role"
        isLoading={changeRoleMutation.isPending}
      />
    </PageContainer>
  );
};
